import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Plus, Tag, CreditCard, Banknote, Smartphone, Check } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { formatINR } from "@/constants/branding";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import AddressDialog from "@/components/AddressDialog";

const PAYMENTS = [
  { id: "cod", label: "Cash on Delivery", desc: "Pay when your order arrives", icon: Banknote, enabled: true },
  { id: "upi", label: "UPI", desc: "Pay online with any UPI app", icon: Smartphone, enabled: true },
  { id: "card", label: "Credit / Debit Card", desc: "Secure payment via Razorpay", icon: CreditCard, enabled: true },
];

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { cart, refreshCart } = useStore();
  const [addresses, setAddresses] = useState([]);
  const [selected, setSelected] = useState(null);
  const [payment, setPayment] = useState("cod");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState(null);
  const [placing, setPlacing] = useState(false);

  const loadAddresses = async () => {
    const { data } = await api.get("/addresses");
    setAddresses(data);
    const def = data.find((a) => a.is_default) || data[0];
    setSelected((s) => s || def?.id || null);
  };

  useEffect(() => {
    loadAddresses();
  }, []);

  useEffect(() => {
    if (!cart.items.length && !placing) navigate("/cart");
  }, [cart.items.length, placing, navigate]);

  const discount = applied?.discount || 0;
  const total = Math.max(0, cart.subtotal - discount);

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    try {
      const { data } = await api.post("/coupons/validate", { code: coupon.trim(), subtotal: cart.subtotal });
      setApplied(data);
      toast.success(`Coupon applied — you saved ${formatINR(data.discount)}`);
    } catch (e) {
      setApplied(null);
      toast.error(apiError(e));
    }
  };

  const placeOrder = async () => {
    if (!selected) return toast.error("Please select a delivery address.");
    setPlacing(true);
    try {
      const { data } = await api.post("/orders", {
        address_id: selected,
        payment_method: payment,
        coupon_code: applied?.code || null,
      });
      await refreshCart();
      if (payment !== "cod") {
        const { data: rz } = await api.post("/payments/razorpay/create", { order_id: data.id });
        const rzp = new window.Razorpay({
          key: rz.key,
          amount: rz.amount,
          currency: rz.currency,
          order_id: rz.razorpay_order_id,
          name: "Zilkart",
          description: "Order " + rz.order_number,
          prefill: { name: rz.name, email: rz.email },
          theme: { color: "#0f5132" },
          handler: async (resp) => {
            try {
              await api.post("/payments/razorpay/verify", {
                order_id: data.id,
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              });
              toast.success("Payment successful!");
              navigate(`/order-success/${data.id}`);
            } catch (err) {
              toast.error(apiError(err));
              setPlacing(false);
            }
          },
          modal: {
            ondismiss: () => {
              toast.error("Payment cancelled. Your order is saved as pending.");
              setPlacing(false);
            },
          },
        });
        rzp.open();
        return;
      }
      toast.success("Order placed successfully!");
      navigate(`/order-success/${data.id}`);
    } catch (e) {
      toast.error(apiError(e));
      setPlacing(false);
    }
  };

  return (
    <div className="shell py-5 sm:py-7 animate-fade-up">
      <h1 className="font-display text-3xl font-bold text-deep mb-5">Checkout</h1>
      <div className="grid lg:grid-cols-3 gap-5 sm:gap-7 items-start">
        <div className="lg:col-span-2 space-y-6">
          {/* Address */}
          <section className="surface p-5 sm:p-7">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold flex items-center gap-2"><MapPin className="h-5 w-5 text-primary" /> Delivery Address</h2>
              <Button variant="outline" size="sm" className="gap-1" onClick={() => setDialogOpen(true)} data-testid="checkout-add-address">
                <Plus className="h-4 w-4" /> Add New
              </Button>
            </div>
            {addresses.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground text-sm" data-testid="checkout-no-address">
                No saved addresses. Please add a delivery address to continue.
              </div>
            ) : (
              <div className="space-y-3">
                {addresses.map((a) => (
                  <label
                    key={a.id}
                    className={`block border rounded-xl p-4 cursor-pointer transition-colors ${selected === a.id ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
                    data-testid={`checkout-address-${a.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <input type="radio" name="addr" checked={selected === a.id} onChange={() => setSelected(a.id)} className="mt-1" />
                      <div className="text-sm">
                        <p className="font-semibold">{a.name} · {a.phone}</p>
                        <p className="text-muted-foreground">{a.line1}{a.line2 ? `, ${a.line2}` : ""}, {a.city}, {a.state} - {a.pincode}</p>
                        {a.is_default && <span className="inline-block mt-1 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">Default</span>}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </section>

          {/* Payment */}
          <section className="surface p-5 sm:p-7">
            <h2 className="font-semibold mb-4 flex items-center gap-2"><CreditCard className="h-5 w-5 text-primary" /> Payment Method</h2>
            <div className="space-y-3">
              {PAYMENTS.map((p) => (
                <label
                  key={p.id}
                  className={`flex items-center gap-3 border rounded-xl p-4 transition-colors ${!p.enabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"} ${payment === p.id ? "border-primary bg-primary/5" : "border-border"}`}
                  data-testid={`checkout-payment-${p.id}`}
                >
                  <input type="radio" name="pay" disabled={!p.enabled} checked={payment === p.id} onChange={() => setPayment(p.id)} />
                  <p.icon className="h-5 w-5 text-muted-foreground" />
                  <div className="text-sm">
                    <p className="font-semibold">{p.label}</p>
                    <p className="text-muted-foreground text-xs">{p.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </section>
        </div>

        {/* Summary */}
        <div className="surface p-6 h-fit lg:sticky lg:top-[130px]">
          <h2 className="font-semibold text-lg mb-4">Price Details</h2>
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="Coupon code" className="pl-9" data-testid="checkout-coupon-input" />
            </div>
            <Button variant="secondary" onClick={applyCoupon} data-testid="checkout-coupon-apply">Apply</Button>
          </div>
          {applied && (
            <p className="text-xs text-success mb-3 flex items-center gap-1"><Check className="h-3 w-3" /> {applied.code} applied</p>
          )}
          <div className="space-y-2 text-sm">
            <Row label={`Subtotal (${cart.count} items)`} value={formatINR(cart.subtotal)} />
            {discount > 0 && <Row label="Discount" value={<span className="text-success">− {formatINR(discount)}</span>} />}
            <Row label="Delivery" value={<span className="text-success">FREE</span>} />
          </div>
          <div className="border-t border-border mt-4 pt-4 flex items-center justify-between font-bold text-lg">
            <span>Total</span>
            <span data-testid="checkout-total">{formatINR(total)}</span>
          </div>
          <Button className="w-full mt-6 h-12 rounded-xl text-sm font-bold" size="lg" disabled={placing || !selected} onClick={placeOrder} data-testid="checkout-place-order-button">
            {placing ? "Placing Order…" : "Place Order"}
          </Button>
        </div>
      </div>

      <AddressDialog open={dialogOpen} onOpenChange={setDialogOpen} onSaved={loadAddresses} />
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium">{value}</span>
  </div>
);
